import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Service from '../services/Service';
import showToast from '../../../../Shared/Utils/ToastNotification';
import '../../PatientList/components/SuggestMedicationModal.css';

const KIT_LABELS = {
  mailman: 'Semen Analysis Kit (MaleMan)',
  tasso: 'Hormone Panel Kit (Tasso)',
};

const ADDRESS_FIELDS = [
  { key: 'name', label: 'Recipient name', required: true },
  { key: 'address1', label: 'Street address', required: true },
  { key: 'address2', label: 'Apt / suite' },
  { key: 'city', label: 'City', required: true },
  { key: 'province_code', label: 'State (2 letters)', required: true, maxLength: 2 },
  { key: 'zip', label: 'ZIP code', required: true, maxLength: 10 },
  { key: 'phone', label: 'Phone', required: true },
];

// Prefill from the order's shipping address, falling back to the patient record
// for orders that carry no address snapshot (re-tests). Once loaded, the street
// address is replaced by the patient's current address on file (see load below).
const initialAddress = (order, patient) => {
  const shipping = order?.shipping_address || {};
  const patientName = [patient?.first_name, patient?.last_name].filter(Boolean).join(' ');
  return {
    name: shipping.name || patientName || '',
    address1: shipping.address1 || patient?.address || '',
    address2: shipping.address2 || patient?.address2 || '',
    city: shipping.city || patient?.city || '',
    province_code: shipping.province_code || patient?.state?.abbreviation || '',
    zip: shipping.zip || patient?.zip_code || '',
    phone: shipping.phone || patient?.phone_number || '',
  };
};

/**
 * "Re-order Kit": sends the patient a $0 replacement kit for an order whose sample
 * could not be tested. The selected kit on this order is cancelled with MaleMan /
 * Tasso (the order itself is marked cancelled once all its kits are replaced, its
 * history is kept), and the replacement is a new order placed with the lab straight away.
 */
const ReorderKitModal = ({ order, patient, onClose, onIssued }) => {
  // Shopify rows carry the Shopify id; the replacement API wants the platform order.
  const dbOrderId = order.db_order?.id || order.id;
  const [kits, setKits] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [selectedKits, setSelectedKits] = useState([]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState(() => initialAddress(order, patient));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await Service.getOrderReplacements(dbOrderId);
        const orderKits = res.data?.kits || [];
        setKits(orderKits);
        setReasons(res.data?.reasons || []);
        // A single-kit order has nothing to choose; a combo starts with nothing
        // ticked so the admin picks the kit that actually failed.
        setSelectedKits(orderKits.length === 1 ? orderKits : []);
        // The patient's current address, which an earlier replacement may have
        // corrected. Name and phone stay from the order; they are per shipment.
        if (res.data?.patient_address) {
          setAddress((prev) => ({ ...prev, ...res.data.patient_address }));
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load re-order options');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dbOrderId]);

  const toggleKit = (kit) => setSelectedKits((prev) => (prev.includes(kit) ? prev.filter((k) => k !== kit) : [...prev, kit]));

  const updateAddress = (key, value) => setAddress((prev) => ({ ...prev, [key]: key === 'province_code' ? value.toUpperCase() : value }));

  const missingRequired = ADDRESS_FIELDS.some((field) => field.required && !String(address[field.key] || '').trim());
  const canSubmit = !loading && !submitting && selectedKits.length > 0 && reason && (reason !== 'other' || notes.trim()) && !missingRequired;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await Service.createOrderReplacement(dbOrderId, {
        kits: selectedKits,
        reason,
        notes: notes.trim(),
        shipping_address: address,
      });
      if (res.data?.placement_failed || res.data?.cancellation_failed) {
        showToast('error', 'Replacement created, but a lab refused a request. See the error on the order and retry.');
      } else {
        showToast('success', 'Replacement kit ordered and the old kit cancelled');
      }
      const addressErrors = Object.keys(res.data?.address_update?.errors || {});
      if (addressErrors.length) {
        const labs = addressErrors.map((lab) => (lab === 'mdi' ? 'MDI' : 'Tasso')).join(' and ');
        showToast('error', `The patient's address was saved, but ${labs} did not accept the update. Update it there manually.`);
      }
      onIssued(res.data);
    } catch (err) {
      // Validation errors (no clinician on the case, bad address...) leave nothing
      // behind, so the admin can fix and resubmit from here.
      setError(err.response?.data?.detail || 'Failed to create the replacement');
    } finally {
      setSubmitting(false);
    }
  };

  const patientName = [patient?.first_name, patient?.last_name].filter(Boolean).join(' ');

  // Rendered into <body>: the order card it opens from gets a hover transform
  // (.order-card:hover), which would pin this fixed overlay to the card and make
  // it jump in and out as the pointer leaves and re-enters the card.
  return createPortal(
    <div className="modal-overlay" onClick={submitting ? undefined : onClose}>
      <div className="modal-content reorder-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Re-order Kit</h2>
          <button className="close-btn" onClick={onClose} disabled={submitting}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p className="patient-info">
            Order <strong>#{order.order_number}</strong>
            {patientName && (
              <>
                {' '}
                · <strong>{patientName}</strong>
              </>
            )}
            {order.email && <> ({order.email})</>}
            <br />
            The replacement is a new order at no charge. The selected kit on this order is cancelled with MaleMan / Tasso; its history is kept.
          </p>

          {loading ? (
            <p>Loading…</p>
          ) : (
            <>
              <div className="reorder-section">
                <h4>Kit to replace</h4>
                {kits.length === 0 && <p className="reorder-hint">This order has no kit that can be replaced.</p>}
                <div className="offerings-list">
                  {kits.map((kit) => (
                    <label key={kit} className={`offering-item ${selectedKits.includes(kit) ? 'selected' : ''}`}>
                      <input type="checkbox" checked={selectedKits.includes(kit)} onChange={() => toggleKit(kit)} />
                      <span>{KIT_LABELS[kit] || kit}</span>
                    </label>
                  ))}
                </div>
                {order.db_order?.has_DNA_product && selectedKits.includes('mailman') && (
                  <p className="reorder-hint">Includes DNA Fragmentation, same as the original kit.</p>
                )}
              </div>

              <div className="reorder-section">
                <h4>Reason</h4>
                <select className="reorder-input" value={reason} onChange={(e) => setReason(e.target.value)}>
                  <option value="">Select a reason</option>
                  {reasons.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <textarea
                  className="reorder-input"
                  rows={3}
                  placeholder={reason === 'other' ? 'Notes (required for Other)' : 'Notes (optional)'}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <div className="reorder-section">
                <h4>Ship to</h4>
                <p className="reorder-hint">
                  Prefilled from the patient&apos;s address on file. A corrected address becomes the patient&apos;s address for future orders too
                  (SwimScore, MDI and Tasso). Recipient name and phone apply to this kit only.
                </p>
                <div className="reorder-address-grid">
                  {ADDRESS_FIELDS.map((field) => (
                    <label key={field.key} className={`reorder-field reorder-field-${field.key}`}>
                      <span>
                        {field.label}
                        {field.required && ' *'}
                      </span>
                      <input
                        className="reorder-input"
                        value={address[field.key]}
                        maxLength={field.maxLength}
                        onChange={(e) => updateAddress(field.key, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {error && <div className="supplement-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Ordering…' : 'Order Replacement Kit'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

ReorderKitModal.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.string.isRequired,
    order_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    shipping_address: PropTypes.object,
    db_order: PropTypes.object,
  }).isRequired,
  patient: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onIssued: PropTypes.func.isRequired,
};

export default ReorderKitModal;
